/**
 * Render Engine (spec section 22).
 *
 * Architecture decision - Canvas + captureStream + MediaRecorder:
 *
 *   audio file -> Web Audio graph (effect) -> MediaStreamAudioDestination
 *   scene      -> Canvas compositor      -> captureStream()
 *   both streams -> MediaStream -> MediaRecorder -> real MP4 or WebM blob
 *
 * Why this and not WebCodecs + a muxer:
 *   - MediaRecorder is baseline-available everywhere (since April 2021)
 *     and needs zero dependencies; the whole studio stays a small bundle.
 *   - WebCodecs needs an external muxer to produce a playable file, and
 *     encoder support varies by device. It is the documented upgrade
 *     path (see ARCHITECTURE.md), not the default.
 *
 * Honest limitation: MediaRecorder is real-time, so exporting a 97 second
 * episode takes about 97 seconds. That is a property of the platform, not
 * of this app, and the UI says so before you start.
 */

import { createCompositor } from './canvas-compositor.js';
import { planLoop, describePlan } from './loop-engine.js';
import { selectRecordingFormat, buildExportFilename, detectContainerFromMime } from './codec.js';
import { sampleFeatures } from '../audio/analyser.js';
import { createLogger } from '../utils/log.js';
import { createTask, isAbortError } from '../utils/async.js';

const log = createLogger('render');

/** Warn the operator before a long real-time export. */
export const LONG_RENDER_WARNING_SECONDS = 300;

export function estimateRender({ audioDurationSeconds, videoBitrateMbps = 8, audioBitrateKbps = 128 }) {
  const seconds = Math.max(0, Number(audioDurationSeconds) || 0);
  const bytes = (seconds * (videoBitrateMbps * 1_000_000 + audioBitrateKbps * 1000)) / 8;
  return {
    seconds,
    estimatedBytes: Math.round(bytes),
    realtimeFactor: 1,
    wallClockSeconds: Math.ceil(seconds),
    isLong: seconds > LONG_RENDER_WARNING_SECONDS,
  };
}

export function createRenderEngine({ audioEngine }) {
  let activeTask = null;

  function cancel(reason = 'cancelled by the operator') {
    if (activeTask) {
      activeTask.cancel(reason);
      return true;
    }
    return false;
  }

  /**
   * Render one BEX CAST episode.
   *
   * @param {object} options
   * @param {HTMLCanvasElement} options.canvas       output canvas (already sized)
   * @param {object} options.scene
   * @param {object} options.settings                app settings snapshot
   * @param {object|null} options.analysis           audio feature table
   * @param {boolean} [options.allowFallback]
   * @param {(progress:object)=>void} [options.onProgress]
   * @param {boolean} [options.previewOnly]          draw frames without recording
   */
  async function render({
    canvas,
    scene,
    settings,
    analysis = null,
    allowFallback = false,
    onProgress = () => {},
    previewOnly = false,
  }) {
    if (!canvas) throw new Error('A render canvas is required.');
    if (!scene) throw new Error('Select a scene before rendering.');
    if (!audioEngine?.buffer) throw new Error('Upload an audio file before rendering.');

    const task = createTask();
    activeTask = task;

    const width = canvas.width;
    const height = canvas.height;
    const fps = Math.min(60, Math.max(12, Number(settings.fps) || 30));
    const duration = audioEngine.duration;

    const sourceKind = scene.assetType === 'video' && scene.assetUrl ? 'video' : 'procedural';
    const loopDuration = sourceKind === 'video' ? Math.max(1, Number(scene.duration) || 10) : Math.max(2, Number(scene.duration) || 10);

    const plan = planLoop({
      audioDuration: duration,
      loopDuration,
      mode: settings.loopMode ?? 'normal',
      crossfadeSeconds: settings.crossfadeSeconds ?? 0.6,
      // Video elements cannot be reversed reliably; procedural scenes can.
      pingPongSupported: sourceKind === 'procedural',
    });

    log.info('render plan', { plan: describePlan(plan), sourceKind, width, height, fps });

    const format = selectRecordingFormat();
    if (!previewOnly && !format.canRecord) {
      throw new Error(format.warning ?? 'Recording is not supported in this browser.');
    }

    const compositor = createCompositor({
      scene,
      plan,
      width,
      height,
      allowFallback,
      fitMode: settings.fitMode ?? 'cover',
      reactiveStrength: (settings.audioReactive ? (settings.audioReactiveStrength ?? 45) : 0) / 100,
    });

    let recorder = null;
    let chunks = [];
    let rafId = null;
    let canvasStream = null;

    const report = (patch) => {
      try {
        onProgress({ totalSeconds: duration, plan, format, ...patch });
      } catch (error) {
        log.warn('progress callback failed', error);
      }
    };

    try {
      report({ phase: 'preparing', progress: 0 });
      await compositor.prepare();

      // Draw one frame before captureStream() so the first captured frame
      // is real content rather than a blank canvas.
      compositor.drawFrame(canvas.getContext('2d'), 0, analysis ? sampleAt(analysis, 0) : null);

      await audioEngine.prepare();
      audioEngine.setEffect(settings.audioEffect ?? 'none', (settings.effectStrength ?? 35) / 100);

      if (!previewOnly) {
        canvasStream = canvas.captureStream(fps);
        const audioStream = audioEngine.getRecordStream();
        const tracks = [...canvasStream.getVideoTracks(), ...audioStream.getAudioTracks()];
        const stream = new MediaStream(tracks);

        recorder = new MediaRecorder(stream, {
          mimeType: format.chosen.mime,
          videoBitsPerSecond: Math.round((settings.videoBitrateMbps ?? 8) * 1_000_000),
          audioBitsPerSecond: Math.round((settings.audioBitrateKbps ?? 128) * 1000),
        });
        recorder.ondataavailable = (event) => {
          if (event.data && event.data.size > 0) chunks.push(event.data);
        };
        const stopped = new Promise((resolve, reject) => {
          recorder.onstop = resolve;
          recorder.onerror = (event) => reject(event.error ?? new Error('MediaRecorder failed.'));
        });
        recorder.start(1000);
        report({ phase: 'recording', progress: 0 });
        // Keep a handle for the cleanup path.
        recorder.__stopped = stopped;
      } else {
        report({ phase: 'previewing', progress: 0 });
      }

      await audioEngine.play({ offset: 0, monitor: Boolean(settings.monitorAudio) });
      await compositor.startAll();

      const ctx = canvas.getContext('2d');
      let lastReportedSecond = -1;
      const wallStartedAt = performance.now();
      const warnings = [];
      let audioClockStalled = false;

      await new Promise((resolve, reject) => {
        const tick = () => {
          try {
            if (task.cancelled) {
              resolve();
              return;
            }

            const wallElapsed = (performance.now() - wallStartedAt) / 1000;
            const audioElapsed = audioEngine.currentTime;

            // The audio clock is the source of truth, but it can stall: no
            // output device, a suspended AudioContext, a Bluetooth sink that
            // disappeared, or a muted browser. If that happens the loop would
            // spin forever, so fall back to wall-clock timing and say so.
            if (!audioClockStalled && wallElapsed > 2 && audioElapsed < wallElapsed * 0.5) {
              audioClockStalled = true;
              warnings.push(
                'The audio clock stopped advancing during the render, so wall-clock timing was used instead. The exported audio may be incomplete on this device.',
              );
              log.warn('audio clock stalled; falling back to wall-clock timing', { audioElapsed, wallElapsed });
            }

            const t = Math.min(duration, audioClockStalled ? wallElapsed : audioElapsed);
            const features = analysis ? sampleAt(analysis, t) : null;
            compositor.drawFrame(ctx, t, features);

            const progress = duration > 0 ? Math.min(1, t / duration) : 0;
            const second = Math.floor(t);
            if (second !== lastReportedSecond) {
              lastReportedSecond = second;
              report({ phase: previewOnly ? 'previewing' : 'recording', progress, elapsedSeconds: t });
            }

            if (t >= duration - 1e-3) {
              resolve();
              return;
            }
            rafId = requestAnimationFrame(tick);
          } catch (error) {
            reject(error);
          }
        };
        rafId = requestAnimationFrame(tick);
      });

      compositor.stopAll();
      audioEngine.stop();
      if (rafId) cancelAnimationFrame(rafId);

      if (previewOnly) {
        report({ phase: 'done', progress: 1, elapsedSeconds: duration });
        return { previewOnly: true, plan, format, warnings, diagnostics: compositor.diagnostics() };
      }

      if (task.cancelled) {
        try {
          recorder.stop();
        } catch {
          /* ignore */
        }
        throw new DOMException(task.reason ?? 'Cancelled', 'AbortError');
      }

      report({ phase: 'finalising', progress: 1, elapsedSeconds: duration });
      recorder.stop();
      await recorder.__stopped;

      const mimeType = recorder.mimeType || format.chosen.mime;
      const blob = new Blob(chunks, { type: mimeType });
      chunks = [];
      if (!blob.size) throw new Error('The recorder produced an empty file. Try a shorter audio clip or a different browser.');

      const container = detectContainerFromMime(mimeType);
      const result = {
        blob,
        url: URL.createObjectURL(blob),
        mimeType,
        container,
        extension: container === 'mp4' ? 'mp4' : 'webm',
        width,
        height,
        fps,
        durationSeconds: duration,
        bytes: blob.size,
        plan,
        format,
        warnings,
        timingSource: audioClockStalled ? 'wall-clock' : 'audio',
        diagnostics: compositor.diagnostics(),
        createdAt: new Date().toISOString(),
      };
      result.filename = buildExportFilename(`bex-cast-${scene.id}`, result.extension);

      log.info('render complete', {
        container,
        bytes: blob.size,
        seconds: duration,
      });

      report({ phase: 'done', progress: 1, elapsedSeconds: duration, result });
      return result;
    } catch (error) {
      if (isAbortError(error)) {
        report({ phase: 'cancelled', progress: 0, error: 'Render cancelled.' });
      } else {
        log.error('render failed', error);
        report({ phase: 'error', progress: 0, error: error.message });
      }
      throw error;
    } finally {
      if (rafId) cancelAnimationFrame(rafId);
      try {
        audioEngine.stop();
      } catch {
        /* ignore */
      }
      compositor.dispose();
      canvasStream?.getTracks().forEach((track) => track.stop());
      activeTask = null;
    }
  }

  return { render, cancel, get isRendering() { return Boolean(activeTask); } };
}

function sampleAt(analysis, seconds) {
  return sampleFeatures(analysis, seconds);
}
